"use client";

import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Mail,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Link2,
  SquarePen,
  Zap,
  ZoomIn,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DialogThumbnailButton } from "@/components/ui/dialog";
import { PhotoModal } from "@/components/ui/photo-modal";
import type { GalleryDress } from "@/types/gallery";
import { useAuth } from "@/context/providers/AuthProvider";
import { getPublicImageUrl } from "@/utils/imageUtils";
import { logger } from "@/utils/logger";
import { trackEvent, trackViewItem, trackAddToCart } from "@/utils/analytics";
import { recordGalleryEngagement } from "@/utils/galleryEngagement";
import { galleryCustomizeHref } from "@/utils/galleryNavigation";
import DressPurchasePricePanel from "@/components/payments/DressPurchasePricePanel";
import {
  formatPublicDressPrice,
  normalizePublicDressPrice,
} from "@/constants/pricing";
import { buildGalleryDressSearchTerms } from "@/lib/designer/amazonSearch";
import {
  getGalleryDressName,
  getGalleryPhotoImages,
} from "./galleryPhotoImages";
import GalleryAmazonLink from "./GalleryAmazonLink";

const loadGalleryPurchaseModal = () =>
  import("@/components/gallery/GalleryPurchaseModal");

const GalleryPurchaseModal = dynamic(loadGalleryPurchaseModal, { ssr: false });

/** Warm the purchase modal chunk before the first click (hover/focus). */
const preloadPurchaseModal = () => {
  void loadGalleryPurchaseModal();
};

interface GalleryDressPageProps {
  dress: GalleryDress;
  /** Listing URL to use for "Back to Gallery" (from `?from=` when linked from the grid). */
  galleryBackHref: string;
  /** Server-rendered breadcrumb trail; replaces the "Back to gallery" link. */
  breadcrumb?: React.ReactNode;
  /** Server-rendered sections shown below the product details. */
  children?: React.ReactNode;
}

function getValidImageUrl(url: string | null | undefined): string | null {
  if (!url || url.trim() === "") {
    return null;
  }
  return url;
}

export default function GalleryDressPage({
  dress,
  galleryBackHref,
  breadcrumb,
  children,
}: GalleryDressPageProps) {
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  // Increments on every open. 0 keeps the modal (and its chunk) unmounted
  // until first needed; keying the modal by it gives each open fresh state
  // while a close still plays the dialog's exit animation.
  const [purchaseSession, setPurchaseSession] = useState(0);
  const [resumeCheckout, setResumeCheckout] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  const copyShareLink = async () => {
    const url = `${window.location.origin}/gallery/dress/${encodeURIComponent(dress.id)}`;
    setShareLink(url);
    setLinkCopied(false);
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
    } catch {
      // Keep a selectable link available when clipboard permission is denied.
    }
  };

  // All images - front, back, then lifestyle variants. Filter out empty/invalid URLs and dedupe.
  const photoImages = useMemo(() => getGalleryPhotoImages(dress), [dress]);
  const images = useMemo(
    () =>
      photoImages
        .map(image => getValidImageUrl(image.src))
        .filter((url): url is string => url !== null),
    [photoImages]
  );

  const [activeImage, setActiveImage] = useState<string | null>(
    () => images[0] ?? null
  );
  const [imageErrorFor, setImageErrorFor] = useState<string | null>(null);
  const { user, roles } = useAuth();
  const userId = user?.id ?? null;
  const itemName = getGalleryDressName(dress);
  const publicMinPrice =
    dress.minPrice === null || dress.minPrice === undefined
      ? null
      : normalizePublicDressPrice(dress.minPrice);

  // Admin title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(dress.title || "");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [currentTitle, setCurrentTitle] = useState(dress.title);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const purchaseIntentTrackedRef = useRef(false);
  const isAdmin = roles?.admin === true;

  const hasImageError = activeImage !== null && imageErrorFor === activeImage;
  const activePhotoIndex = Math.max(
    0,
    photoImages.findIndex(image => image.src === activeImage)
  );

  const trackPurchaseIntent = useCallback(
    (source: "buy_now" | "purchase_param") => {
      if (purchaseIntentTrackedRef.current) {
        return;
      }

      purchaseIntentTrackedRef.current = true;

      trackEvent("gallery_ticket_purchase_intent", {
        ticket_id: dress.id,
        item_id: dress.id,
        item_name: itemName,
        item_category: dress.preset_type || "gallery",
        value: publicMinPrice ?? 0,
        currency: dress.currency || "USD",
        gallery_source: source,
      });

      // Standard GA4 ecommerce event so Shopping bidding / audiences see this
      // dress-purchase intent. item_category mirrors view_item (the dress's
      // preset type) so the same dress has one consistent category across the
      // funnel; it is never "credit", keeping dress vs credit distinct.
      trackAddToCart({
        currency: dress.currency || "USD",
        value: publicMinPrice ?? 0,
        items: [
          {
            item_id: dress.id,
            item_name: itemName,
            item_category: dress.preset_type || "gallery",
            price: publicMinPrice ?? 0,
            quantity: 1,
          },
        ],
      });

      recordGalleryEngagement({
        ticketId: dress.id,
        eventName: "gallery_ticket_purchase_intent",
      });
    },
    [dress.currency, dress.id, publicMinPrice, dress.preset_type, itemName]
  );

  const openPurchaseModal = useCallback((fromCheckoutCancel = false) => {
    setResumeCheckout(fromCheckoutCancel);
    setPurchaseSession(session => session + 1);
    setShowPurchaseModal(true);
  }, []);

  useEffect(() => {
    setActiveImage(current =>
      current && images.includes(current) ? current : (images[0] ?? null)
    );
    setImageErrorFor(current =>
      current && images.includes(current) ? current : null
    );
  }, [images]);

  useEffect(() => {
    const itemCategory = dress.preset_type || "gallery";
    const value = publicMinPrice ?? 0;
    const currency = dress.currency || "USD";

    trackViewItem({
      currency,
      value,
      items: [
        {
          item_id: dress.id,
          item_name: itemName,
          item_category: itemCategory,
          price: value,
          quantity: 1,
        },
      ],
    });

    trackEvent("gallery_ticket_view", {
      ticket_id: dress.id,
      item_id: dress.id,
      item_name: itemName,
      item_category: itemCategory,
      value,
      currency,
      gallery_source: "detail_page",
    });

    recordGalleryEngagement({
      ticketId: dress.id,
      eventName: "gallery_ticket_view",
    });
  }, [dress.currency, dress.id, publicMinPrice, dress.preset_type, itemName]);

  useEffect(() => {
    let activeStartedAt =
      typeof document !== "undefined" && document.visibilityState === "visible"
        ? Date.now()
        : null;
    let activeMs = 0;

    const accumulateActiveTime = () => {
      if (activeStartedAt === null) {
        return;
      }
      activeMs += Date.now() - activeStartedAt;
      activeStartedAt = Date.now();
    };

    const flushEngagement = (reason: string, useBeacon = false) => {
      accumulateActiveTime();
      const seconds = Math.round(activeMs / 1000);
      if (seconds < 5) {
        return;
      }

      activeMs = 0;

      trackEvent("gallery_ticket_engagement", {
        ticket_id: dress.id,
        item_id: dress.id,
        item_name: itemName,
        item_category: dress.preset_type || "gallery",
        time_spent_seconds: seconds,
        engagement_time_msec: seconds * 1000,
        engagement_reason: reason,
      });

      recordGalleryEngagement(
        {
          ticketId: dress.id,
          eventName: "gallery_ticket_engagement",
          timeSpentSeconds: seconds,
        },
        { beacon: useBeacon }
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushEngagement("visibility_hidden", true);
        activeStartedAt = null;
      } else if (activeStartedAt === null) {
        activeStartedAt = Date.now();
      }
    };

    const handlePageHide = () => {
      flushEngagement("pagehide", true);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      flushEngagement("unmount", true);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [dress.id, dress.preset_type, itemName]);

  // Check if user owns this ticket. Keyed on the user id: AuthProvider hands
  // out a new user object on every session refresh, which must not re-run it.
  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();

    const checkOwnership = async () => {
      if (!userId || !dress.id) {
        setIsOwner(false);
        return;
      }

      try {
        const response = await fetch(`/api/tickets/${dress.id}/ownership`, {
          signal: controller.signal,
        });
        if (response.ok) {
          const data = await response.json();
          if (isActive) {
            setIsOwner(data.isOwner);
          }
        }
      } catch {
        if (controller.signal.aborted) {
          return;
        }
        // Silently fail - ownership check is optional
        if (isActive) {
          setIsOwner(false);
        }
      }
    };

    checkOwnership();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [userId, dress.id]);

  // Auto-open the purchase modal from the URL: `?purchase=true` (legacy deep
  // link) or `?purchase=cancelled` (Stripe checkout cancel_url, which reopens
  // the saved draft without counting a second add-to-cart).
  useEffect(() => {
    if (typeof window === "undefined" || !dress.hasQuotes) return;

    const purchaseParam = new URLSearchParams(window.location.search).get(
      "purchase"
    );
    if (purchaseParam !== "true" && purchaseParam !== "cancelled") return;

    if (purchaseParam === "cancelled") {
      trackEvent("gallery_checkout_cancelled", {
        ticket_id: dress.id,
        item_id: dress.id,
        gallery_source: "stripe_cancel",
      });
    } else {
      trackPurchaseIntent("purchase_param");
    }
    openPurchaseModal(purchaseParam === "cancelled");
    // Clean up URL
    window.history.replaceState({}, "", window.location.pathname);
  }, [dress.hasQuotes, dress.id, openPurchaseModal, trackPurchaseIntent]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Handle admin title save
  const handleSaveTitle = async () => {
    if (!isAdmin) return;

    const trimmedTitle = editedTitle.trim();

    // Don't save if unchanged
    if (trimmedTitle === (currentTitle || "")) {
      setIsEditingTitle(false);
      return;
    }

    setIsSavingTitle(true);
    setTitleError(null);
    try {
      const response = await fetch(`/api/admin/tickets/${dress.id}/actions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          action: "title",
          title: trimmedTitle,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update title");
      }

      // Update local state on success
      setCurrentTitle(trimmedTitle || null);
      setIsEditingTitle(false);
      logger.info("Admin updated dress title", {
        component: "GalleryDressPage",
        metadata: { dressId: dress.id, newTitle: trimmedTitle },
      });
    } catch (error) {
      logger.error(
        "Failed to update dress title",
        { component: "GalleryDressPage", metadata: { dressId: dress.id } },
        error instanceof Error ? error : new Error(String(error))
      );
      // Keep the editor open with the attempted title so it can be retried.
      setTitleError(
        error instanceof Error ? error.message : "Failed to update title"
      );
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Handle cancel editing
  const handleCancelEdit = () => {
    setTitleError(null);
    setEditedTitle(currentTitle || "");
    setIsEditingTitle(false);
  };

  // Handle keyboard events in title input
  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveTitle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  // Get first 3 words of title or prompt for breadcrumb
  const getBreadcrumbText = () => {
    const displayText = currentTitle || dress.prompt;
    if (!displayText) return "Dress";
    const words = displayText.trim().split(/\s+/);
    if (words.length <= 3) {
      return displayText;
    }
    return words.slice(0, 3).join(" ") + "...";
  };

  // Display title (use currentTitle for live updates)
  const displayTitle = getGalleryDressName({
    title: currentTitle,
    prompt: dress.prompt,
  });

  const designDetails = [
    { label: "Length", value: dress.dress_length },
    { label: "Sleeve", value: dress.sleeve_style },
    { label: "Neckline", value: dress.neckline_style },
    { label: "Waistline", value: dress.waistline_style },
    { label: "Silhouette", value: dress.silhouette_fit },
    { label: "Fabric", value: dress.fabric_texture },
  ].filter(detail => !!detail.value);

  // The route 404s dresses without a current quote, so every rendered dress
  // is purchasable here.
  const handlePrimaryPurchase = () => {
    trackPurchaseIntent("buy_now");
    openPurchaseModal();
  };

  const showStickyBuyBar = dress.hasQuotes && publicMinPrice !== null;

  return (
    <div
      className={`min-h-screen bg-background ${
        showStickyBuyBar ? "pb-28 lg:pb-safe-navigation" : "pb-safe-navigation"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Navigation & Breadcrumb */}
        {breadcrumb ? (
          <div className="mb-6">{breadcrumb}</div>
        ) : (
          <div className="flex items-center justify-between mb-6">
            <nav className="flex items-center text-sm text-muted-foreground">
              <Link
                href={galleryBackHref}
                className="hover:text-primary transition-colors flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" aria-hidden />
                Back to gallery
              </Link>
              <span className="mx-2 text-muted-foreground">/</span>
              <span className="text-foreground font-medium truncate max-w-[150px] sm:max-w-xs">
                {getBreadcrumbText()}
              </span>
            </nav>
          </div>
        )}

        {/* Owner banner */}
        {isOwner && (
          <div className="mb-8">
            <Card variant="muted" padding="compact">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-muted flex items-center justify-center text-primary">
                    <ShoppingBag className="w-6 h-6" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      This is your order
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Track your order and message your manufacturer.
                    </p>
                  </div>
                </div>
                <Button asChild variant="default" size="sm">
                  <Link href={`/ticket/${dress.id}`} className="gap-2">
                    View your order
                    <ChevronRight className="w-4 h-4" aria-hidden />
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Image Section - 7/12 columns on desktop */}
          <div className="lg:col-span-7 space-y-4">
            <div className="relative aspect-3/4 w-full overflow-hidden bg-muted rounded-2xl shadow-sm border border-border">
              {activeImage && !hasImageError ? (
                <>
                  <Image
                    src={activeImage}
                    alt={displayTitle}
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 100vw, 60vw"
                    priority
                    onError={() => {
                      // Try original_image_path when it resolves to a
                      // different URL; otherwise show the error state.
                      const validFallback = dress.original_image_path
                        ? getValidImageUrl(
                            getPublicImageUrl(dress.original_image_path)
                          )
                        : null;
                      if (validFallback && validFallback !== activeImage) {
                        setActiveImage(validFallback);
                        setImageErrorFor(null);
                      } else {
                        setImageErrorFor(activeImage);
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPhotoModal(true)}
                    className="absolute inset-0 z-10 cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                    aria-label="Open gallery image preview"
                  >
                    <span className="absolute bottom-4 right-4 inline-flex size-11 items-center justify-center rounded-full border border-white/20 bg-background/80 text-foreground shadow-sm backdrop-blur-md transition-colors hover:bg-background">
                      <ZoomIn className="size-4" aria-hidden />
                    </span>
                  </button>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  {hasImageError
                    ? "Failed to load image"
                    : "No image available"}
                </div>
              )}

              {/* Price Badge */}
              {dress.hasQuotes && publicMinPrice != null && (
                <div className="absolute top-4 left-4">
                  <Badge variant="success" size="md">
                    {formatPublicDressPrice(
                      publicMinPrice,
                      dress.currency || "USD"
                    )}
                  </Badge>
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div>
                <div
                  className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 scrollbar-hide"
                  aria-label="Dress views. Scroll horizontally for more images."
                >
                  {images.map((img, idx) => (
                    <DialogThumbnailButton
                      key={idx}
                      onClick={() => {
                        setActiveImage(img);
                        setImageErrorFor(null);
                      }}
                      selected={activeImage === img}
                      className="w-24 snap-start"
                      aria-label={`View gallery image ${idx + 1}`}
                    >
                      <Image
                        src={img}
                        alt={`${displayTitle}, view ${idx + 1}`}
                        fill
                        className="object-cover"
                        sizes="96px"
                        onError={() => {
                          // Silently handle thumbnail errors - don't break the UI
                        }}
                      />
                    </DialogThumbnailButton>
                  ))}
                </div>
                {images.length > 3 ? (
                  <p className="mt-1 text-right text-xs text-muted-foreground sm:hidden">
                    Swipe for more views →
                  </p>
                ) : null}
              </div>
            )}
          </div>

          {/* Details Section - 5/12 columns on desktop */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-8 space-y-8">
              {/* Product Header */}
              <div className="space-y-4">
                {/* Admin Editable Title */}
                {isAdmin && isEditingTitle ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        ref={titleInputRef}
                        type="text"
                        value={editedTitle}
                        onChange={e => setEditedTitle(e.target.value)}
                        onKeyDown={handleTitleKeyDown}
                        placeholder="Enter dress title..."
                        maxLength={100}
                        disabled={isSavingTitle}
                        className="flex-1 text-xl sm:text-2xl font-bold h-auto py-2"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleSaveTitle}
                        variant="default"
                        size="sm"
                        disabled={isSavingTitle}
                      >
                        {isSavingTitle ? "Saving..." : "Save"}
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        variant="outline"
                        size="sm"
                        disabled={isSavingTitle}
                      >
                        Cancel
                      </Button>
                      <span className="text-xs text-muted-foreground ml-2">
                        {editedTitle.length}/100 characters
                      </span>
                    </div>
                    {titleError ? (
                      <p role="alert" className="text-sm text-destructive">
                        {titleError}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="group relative">
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight">
                      {displayTitle}
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditedTitle(currentTitle || "");
                            setTitleError(null);
                            setIsEditingTitle(true);
                          }}
                          className="ml-2 inline-flex items-center justify-center w-8 h-8 rounded-full
                                     text-muted-foreground hover:text-primary hover:bg-primary-muted
                                     opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-all duration-200"
                          title="Edit title (Admin)"
                          aria-label="Edit dress title"
                        >
                          <SquarePen className="w-4 h-4" aria-hidden />
                        </button>
                      )}
                    </h1>
                    {isAdmin && (
                      <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                        Use the pencil button to edit the title.
                      </span>
                    )}
                  </div>
                )}
                {dress.prompt && (
                  <div className="space-y-2">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                      Design description
                    </h2>
                    <p className="text-sm text-foreground leading-relaxed">
                      {dress.prompt}
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <Button variant="outline" size="sm" onClick={copyShareLink}>
                    <Link2 className="size-4" aria-hidden />
                    Copy share link
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Anyone with the link can view this dress. No sign-in
                    required.
                  </p>
                  {shareLink && (
                    <div className="space-y-1">
                      <p
                        role="status"
                        className="text-xs text-muted-foreground"
                      >
                        {linkCopied
                          ? "Link copied"
                          : "Select and copy the link below"}
                      </p>
                      <Input
                        aria-label="Dress share link"
                        readOnly
                        value={shareLink}
                        onFocus={event => event.currentTarget.select()}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Price Information */}
              {dress.hasQuotes && publicMinPrice !== null && (
                <DressPurchasePricePanel
                  amount={publicMinPrice || 0}
                  currency={dress.currency || "USD"}
                />
              )}

              {/* Primary CTA */}
              <div className="space-y-3">
                <Button
                  onClick={handlePrimaryPurchase}
                  onPointerEnter={preloadPurchaseModal}
                  onFocus={preloadPurchaseModal}
                  variant="success"
                  size="lg"
                  className="w-full h-14 text-lg font-semibold shadow-md hover:shadow-lg transition-all"
                >
                  Buy now
                </Button>
                <Button asChild variant="outline" size="lg" fullWidth>
                  <Link
                    href={galleryCustomizeHref(dress, "gallery_detail")}
                    className="gap-2"
                  >
                    <SquarePen className="size-4" aria-hidden />
                    Customize this dress
                  </Link>
                </Button>
                <GalleryAmazonLink
                  searchTerms={buildGalleryDressSearchTerms(dress)}
                  placement="gallery_detail"
                />
                {isAdmin && (
                  <Button asChild variant="outline" size="lg" fullWidth>
                    <Link href={`/ticket/${dress.id}`} className="gap-2">
                      View request
                      <ChevronRight className="w-4 h-4" aria-hidden />
                    </Link>
                  </Button>
                )}
              </div>

              {/* Design Details Card */}
              {designDetails.length > 0 && (
                <Card variant="muted-subtle">
                  <CardContent>
                    <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
                      Design specifications
                    </h2>
                    <dl className="divide-y divide-border/60">
                      {designDetails.map(detail => (
                        <div
                          key={detail.label}
                          className="py-2.5 first:pt-0 last:pb-0"
                        >
                          <dt className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide">
                            {detail.label}
                          </dt>
                          <dd className="mt-1 text-xs text-foreground font-medium leading-snug">
                            {detail.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </CardContent>
                </Card>
              )}

              {/* Trust Features */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card
                  variant="default"
                  padding="compact"
                  className="min-w-0 flex-row items-start gap-3"
                >
                  <div className="w-8 h-8 rounded-lg bg-success-muted flex items-center justify-center text-success shrink-0">
                    <ShieldCheck className="w-5 h-5" aria-hidden />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <h4 className="text-xs font-bold text-foreground">
                      Quality guarantee
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Full refund for defects or quality issues
                    </p>
                  </div>
                </Card>
                <Card
                  variant="default"
                  padding="compact"
                  className="min-w-0 flex-row items-start gap-3"
                >
                  <div className="w-8 h-8 rounded-lg bg-info-muted flex items-center justify-center text-info shrink-0">
                    <Zap className="w-5 h-5" aria-hidden />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <h4 className="text-xs font-bold text-foreground">
                      Made to measure
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Made to your exact measurements
                    </p>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>

        {/* Additional purchase details */}
        <details className="group mt-10 border-t border-border pt-6 sm:mt-14">
          <summary className="flex cursor-pointer list-none items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted [&::-webkit-details-marker]:hidden">
            <span>More information</span>
            <ChevronDown
              className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180"
              aria-hidden="true"
            />
          </summary>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              {
                step: "1",
                title: "Start checkout",
                desc: "Select “Buy now” to review your instant quote and start your order.",
                icon: ShoppingCart,
              },
              {
                step: "2",
                title: "Add payment and measurements",
                desc: "Enter your measurements and payment details to place your order.",
                icon: ClipboardList,
              },
              {
                step: "3",
                title: "Check your email",
                desc: "We’ll email your order confirmation and updates as your dress is made.",
                icon: Mail,
              },
            ].map((item, idx) => {
              const StepIcon = item.icon;

              return (
                <div key={idx} className="relative group">
                  <Card
                    variant="muted-subtle"
                    padding="compact"
                    className="flex flex-col items-center text-center"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-card border border-border shadow-sm flex items-center justify-center text-primary group-hover:bg-primary/10 transition-colors duration-300">
                      <StepIcon className="w-8 h-8" aria-hidden />
                    </div>
                    <h3 className="text-lg font-bold text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </Card>
                  {idx < 2 && (
                    <div className="hidden md:block absolute top-8 left-[calc(100%-1rem)] w-full h-[2px] bg-muted -z-10" />
                  )}
                </div>
              );
            })}
          </div>
        </details>
        {children}
      </div>

      {showStickyBuyBar ? (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 shadow-[0_-12px_30px_rgba(0,0,0,0.2)] backdrop-blur lg:hidden">
          <div className="mx-auto flex max-w-lg items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Total price</p>
              <p className="truncate text-lg font-bold text-foreground">
                {formatPublicDressPrice(
                  publicMinPrice,
                  dress.currency || "USD"
                )}
              </p>
            </div>
            <Button
              onClick={handlePrimaryPurchase}
              onPointerEnter={preloadPurchaseModal}
              onFocus={preloadPurchaseModal}
              variant="success"
              size="xl"
              className="min-w-36"
            >
              Buy now
            </Button>
          </div>
        </div>
      ) : null}

      <PhotoModal
        open={showPhotoModal}
        onOpenChange={setShowPhotoModal}
        images={photoImages}
        activeIndex={activePhotoIndex}
        onActiveIndexChange={index => {
          const nextImage = photoImages[index]?.src;
          if (!nextImage) return;
          setActiveImage(nextImage);
          setImageErrorFor(null);
        }}
        title={displayTitle}
      />

      {/* Purchase Modal: mounted on first open, fresh instance per open. */}
      {purchaseSession > 0 ? (
        <GalleryPurchaseModal
          key={purchaseSession}
          isOpen={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
          dress={dress}
          resumeCheckout={resumeCheckout}
        />
      ) : null}
    </div>
  );
}
